import './styles.css'

import { DeleteOutlined, ExclamationCircleFilled, FilePdfOutlined, PaperClipOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Button, Image, List, Popconfirm, Spin, Upload } from 'antd'
import { PdfViewer } from '../PDFViewer/PDFViewer'

type BaseImage = {
  url: string
  file_type: string
}

type ImageViewerProps<TImage extends BaseImage> = {
  images: TImage[]
  selectedImageIndex: number
  setSelectedImageIndex: (index: number) => void
  isImageUploading: boolean
  onDelete: () => void
  onUpload: (file: File) => Promise<any>
  canEdit?: boolean
}

type ImagePreviewListProps<TImage> = {
  images: TImage[]
  selectedImageIndex: number
  setSelectedImageIndex: (index: number) => void
}

function isPdf<T extends BaseImage>(image?: T) {
  return image?.file_type === 'pdf'
}

export function ImageViewer<TImage extends BaseImage>({
  images,
  selectedImageIndex,
  setSelectedImageIndex,
  isImageUploading,
  onDelete,
  onUpload,
  canEdit,
}: ImageViewerProps<TImage>) {
  return (
    <Vertical className='image-viewer' justifyContent='space-between'>
      {images?.length ? (
        <>
          {isPdf(images?.[selectedImageIndex]) ? (
            <PdfViewer src={images[selectedImageIndex].url} />
          ) : (
            <Image
              className='image-viewer-image'
              src={images?.[selectedImageIndex]?.url}
              alt={`Attachment ${selectedImageIndex + 1}`}
              height='100%'
              width='100%'
            />
          )}

          {canEdit && (
            <Popconfirm
              title='Delete Attachment'
              description='Are you sure you want to delete?'
              onConfirm={onDelete}
              okText='Yes'
              cancelText='No'
              icon={<ExclamationCircleFilled style={{ color: 'var(--theme-error)' }} />}
              okButtonProps={{ danger: true }}
              classNames={{ body: 'image-viewer-popconfirm' }}
            >
              <Button className='image-viewer-delete-button' color='danger' variant='solid' icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </>
      ) : (
        <Upload className='image-viewer-upload' showUploadList={false} beforeUpload={onUpload} disabled={!canEdit}>
          <Vertical
            style={{ background: 'var(--gray-200)' }}
            height='100%'
            width='100%'
            verticalCenter
            horizontalCenter
          >
            <Button color='default' variant='link' style={{ height: '100%', width: '100%' }}>
              <Vertical height='100%' verticalCenter horizontalCenter>
                {isImageUploading ? (
                  <Spin />
                ) : (
                  <>
                    <PaperClipOutlined style={{ fontSize: 32 }} />
                    <Texto category='h5'>No Attachments</Texto>
                  </>
                )}
              </Vertical>
            </Button>
          </Vertical>
        </Upload>
      )}
      <ImagePreviewList
        images={images ?? []}
        selectedImageIndex={selectedImageIndex}
        setSelectedImageIndex={setSelectedImageIndex}
      />
    </Vertical>
  )
}

function ImagePreviewList<TImage extends BaseImage>({
  images,
  selectedImageIndex,
  setSelectedImageIndex,
}: ImagePreviewListProps<TImage>) {
  return (
    <Horizontal className='p-2 image-viewer-preview-container' gap='1em' alignItems='center'>
      {images.map((image: TImage, index: number) => (
        <List.Item
          onClick={() => setSelectedImageIndex(index)}
          className={`image-viewer-preview ${selectedImageIndex === index ? 'selected' : ''}`}
          key={image?.url}
        >
          <List.Item.Meta
            avatar={
              isPdf(image) ? (
                <Horizontal style={{ width: 60, height: 60 }} verticalCenter horizontalCenter>
                  <FilePdfOutlined style={{ fontSize: 48 }} />
                </Horizontal>
              ) : (
                <Image src={image?.url} width={60} height={60} preview={false} />
              )
            }
          />
        </List.Item>
      ))}
    </Horizontal>
  )
}
